import { z } from 'zod';
import {
  idempotencyKeySchema,
  pageSchema,
  statusToneSchema,
  timestampSchema,
  uuidSchema,
  versionedSchema,
} from './common';
import type { StatusTone } from './common';

/**
 * The project register: risks, opportunities, ideas, decisions and issues
 * (docs/meetings-design.md, section 4).
 *
 * These are the things a project keeps a list of. Structurally they are the
 * same row -- a title, a description, an owner, a status, a date, an ordinal,
 * and a link to whatever it is about -- differing in vocabulary and in two or
 * three fields each.
 *
 * Written as five modules that would be five migrations, five services, five
 * screens, five link providers and five sets of translations, to express a
 * difference that amounts to whether the ordinal is called "severity" or
 * "priority". So there is one table and one catalog, in the same shape as
 * `COMPONENT_CATALOG`: shared columns, plus a declared Zod schema for the
 * kind-specific remainder that the API validates and the frontend builds a form
 * from.
 *
 * Two rules carry the design and are worth stating before the schemas:
 *
 *  1. **A field the list sorts, filters or aggregates on is a column;
 *     everything else is declared JSON.** A risk register is read by sorting on
 *     severity against probability and filtering on status and owner, and none
 *     of that survives being in a blob without a functional index nobody will
 *     maintain. A decision's "who else was in the room" is read one row at a
 *     time and never aggregated, so it is `attributes`.
 *
 *  2. **Each kind is its own entity kind, not a subtype.** The cross-reference
 *     picker offers a list of kinds and "register item" is not a thing anybody
 *     is looking for; the linked-items panel groups by kind, and a group mixing
 *     three risks with a decision reads as a filing error. See
 *     `LINK_ENTITY_CATALOG` in links.ts, where all five appear.
 */

/* --------------------------------------------------------------- the kinds */

export const REGISTER_KINDS = [
  'risk',
  'opportunity',
  'idea',
  'decision',
  'issue',
  'observation',
] as const;
export const registerKindSchema = z.enum(REGISTER_KINDS);
export type RegisterKind = (typeof REGISTER_KINDS)[number];

export function isRegisterKind(value: string): value is RegisterKind {
  return (REGISTER_KINDS as readonly string[]).includes(value);
}

/**
 * Where an item is in its life.
 *
 * One vocabulary for all five kinds, which is a compromise and a deliberate
 * one: a decision is never "in progress" and an idea is rarely "resolved". Five
 * status enums would make the list screen's filter kind-dependent and the
 * dashboard tile's rollup impossible, for a gain of a few unused members. The
 * catalog names which statuses a kind actually offers (`statuses`), so the form
 * shows three for a decision and five for a risk without the storage knowing.
 */
export const REGISTER_STATUSES = ['open', 'in_progress', 'resolved', 'closed', 'rejected'] as const;
export const registerStatusSchema = z.enum(REGISTER_STATUSES);
export type RegisterStatus = (typeof REGISTER_STATUSES)[number];

/* ------------------------------------------------------- kind-specific data */

/**
 * The kind-specific remainder, one schema per kind.
 *
 * Every member is optional and bounded. They are prose fields that a reader
 * writes once and reads later; nothing here is ever sorted or counted, which is
 * exactly the test for belonging in JSON rather than in a column.
 */
const riskAttributes = z
  .object({
    /** What is being done so the risk does not happen. */
    mitigation: z.string().max(2000).nullish(),
    /** What will be done if it happens anyway. */
    contingency: z.string().max(2000).nullish(),
  })
  .strict();

const opportunityAttributes = z
  .object({
    /** What would have to be done to capture it. */
    action: z.string().max(2000).nullish(),
  })
  .strict();

const ideaAttributes = z
  .object({
    /** What would have to be true for this to be worth doing. */
    benefit: z.string().max(2000).nullish(),
  })
  .strict();

const decisionAttributes = z
  .object({
    /** Why, in the author's words. The single most valuable field in the whole
     *  register: a decision without its reasoning is re-litigated every time
     *  somebody new reads it. */
    rationale: z.string().max(4000).nullish(),
    /**
     * Who decided, when that is not a user account -- a steering group, a
     * client representative, a name from the minutes. Free text on purpose:
     * the deciding body is frequently not a person with a login, and a
     * nullable user reference would quietly record "nobody".
     */
    decidedBy: z.string().max(200).nullish(),
  })
  .strict();

/**
 * A safety observation: something somebody saw on site.
 *
 * The sixth kind, and the one that most clearly earned the catalog. Structurally
 * it is the same row as the other five -- a title, a description, an owner, a
 * status, a date and an ordinal -- and every field below is prose read one row
 * at a time, which is exactly the test for belonging in `attributes`.
 *
 * `location` is free text rather than a linear reference or a map feature. An
 * observation is written down by whoever saw the thing, frequently on a phone,
 * frequently before anybody knows which chainage it corresponds to; demanding a
 * position is how a safety register stops being filled in. A row that deserves
 * a position gets one the way everything else does, through a link.
 */
const observationAttributes = z
  .object({
    /** Where, in the observer's own words: "north abutment, under the deck". */
    location: z.string().max(500).nullish(),
    /** What was done there and then, before anybody filed anything. */
    immediateAction: z.string().max(2000).nullish(),
    /** What has to change so it does not recur. */
    correctiveAction: z.string().max(2000).nullish(),
  })
  .strict();

const issueAttributes = z
  .object({
    /** What it is costing while it is open. */
    impact: z.string().max(2000).nullish(),
  })
  .strict();

/**
 * One kind-specific prose field, as the form needs to draw it.
 *
 * The Zod schema beside this one says what the API will *accept*; this says
 * what the form should *offer*, which is not the same question and cannot be
 * answered from the schema alone -- a schema has no opinion about what a field
 * is called or whether it deserves three rows or one.
 *
 * They must nonetheless agree about which keys exist, or a declared field would
 * be rendered and refused, or accepted and never shown. `register.test.ts`
 * asserts the two key sets are identical rather than trusting them to stay so.
 */
export interface RegisterAttributeField {
  /** The key inside `attributes`. Also the tracked field name in a revision,
   *  as `attributes.<key>`. */
  key: string;
  labelKey: string;
  /** Matches the schema's own bound. Stated here so the textarea can stop
   *  typing at the same place the API would refuse it. */
  maxLength: number;
  /** A rationale is a paragraph and a deciding body is a line. */
  multiline: boolean;
  /**
   * Whether this field gets the Markdown toolbar and renders through
   * `renderMarkdown` for display, rather than a plain `<textarea>`
   * (docs/rich-text-design.md section 4.2). Defaults to `'plain'` when
   * absent -- a one-line-or-short field (`decidedBy`) has no business with
   * a formatting toolbar, so only the prose fields declare `'markdown'`
   * explicitly, the same "declare the exception, not the common case" shape
   * `multiline` itself already uses.
   */
  format?: 'plain' | 'markdown';
}

export interface RegisterKindDefinition {
  kind: RegisterKind;
  /** Translation keys for the singular and plural name of the kind. Both are
   *  needed: a tab says "Risks", a card says "Risk". */
  labelKey: string;
  pluralKey: string;
  /**
   * The letter a reader quotes in a meeting. Reference numbers are derived --
   * `R-014` is this prefix and a per-project, per-kind sequence -- rather than
   * stored as a string, so renaming a prefix is a catalog edit and not a data
   * migration.
   */
  referencePrefix: string;
  /** Which of the shared optional columns this kind uses. A field that is off
   *  is not rendered, not writable, and null in storage. */
  fields: {
    severity: boolean;
    probability: boolean;
    dueOn: boolean;
    occurredOn: boolean;
  };
  /**
   * What the severity ordinal is called here. A risk has a severity, an idea
   * has a priority, and calling both "severity" in the interface is how a
   * shared column becomes a confusing one.
   */
  severityLabelKey: string | null;
  /**
   * What the deadline is called here, and null for a kind that has none.
   *
   * The same reasoning as `severityLabelKey`, one field along: "due date" is
   * right for a risk's review and wrong for a decision, where the sentence
   * people say is "decide by". A shared label would be the phrase nobody is
   * looking for, and a `switch` in the form would be the kind catalog not doing
   * its job (docs/register-collaboration-design.md section 5.2).
   *
   * Non-null exactly when `fields.dueOn` is true, which `register.test.ts`
   * asserts rather than trusting.
   */
  dueOnLabelKey: string | null;
  /**
   * What the shared event date is called here, and null for a kind that has
   * none.
   *
   * The same reasoning as `severityLabelKey` and `dueOnLabelKey`, one column
   * along. One nullable date means "the day the thing this row records actually
   * happened, which is not the day somebody typed it up" -- a decision is
   * *decided on*, an observation is *observed on*, and a shared label would be
   * the phrase neither reader is looking for.
   *
   * The column was called `decidedOn` while a decision was the only kind that
   * used it. Adding a second one made the name a lie, so it was renamed rather
   * than reused under a comment apologising for it.
   *
   * Non-null exactly when `fields.occurredOn` is true, which `register.test.ts`
   * asserts rather than trusting.
   */
  occurredOnLabelKey: string | null;
  /** The statuses this kind offers, in the order the form lists them. */
  statuses: readonly RegisterStatus[];
  attributesSchema: z.ZodType;
  /**
   * The same fields, declared for rendering and for change tracking.
   *
   * Until this existed the kind-specific prose was written by the API and
   * offered by nothing: a decision's rationale -- "the single most valuable
   * field in the whole register", by the comment on its own schema above --
   * could not be entered anywhere in the product. A form generated from a
   * declaration is what makes that a catalog row rather than a screen.
   */
  attributeFields: readonly RegisterAttributeField[];
  /** Bumped when the attributes shape changes, so stored values can be
   *  migrated. Same role as `ComponentDefinition.configVersion`. */
  attributesVersion: number;
  order: number;
}

export const REGISTER_KIND_CATALOG = {
  risk: {
    kind: 'risk',
    labelKey: 'register.kind.risk',
    pluralKey: 'register.kind.risk_plural',
    referencePrefix: 'R',
    fields: { severity: true, probability: true, dueOn: true, occurredOn: false },
    severityLabelKey: 'register.field.severity',
    dueOnLabelKey: 'register.field.reviewBy',
    occurredOnLabelKey: null,
    statuses: ['open', 'in_progress', 'resolved', 'closed'],
    attributesSchema: riskAttributes,
    attributeFields: [
      {
        key: 'mitigation',
        labelKey: 'register.attribute.mitigation',
        maxLength: 2000,
        multiline: true,
        format: 'markdown',
      },
      {
        key: 'contingency',
        labelKey: 'register.attribute.contingency',
        maxLength: 2000,
        multiline: true,
        format: 'markdown',
      },
    ],
    attributesVersion: 1,
    order: 10,
  },
  opportunity: {
    kind: 'opportunity',
    labelKey: 'register.kind.opportunity',
    pluralKey: 'register.kind.opportunity_plural',
    referencePrefix: 'O',
    fields: { severity: true, probability: true, dueOn: true, occurredOn: false },
    /* The same column, and honestly a different concept: an opportunity's
     * ordinal is how much it is worth, not how much it hurts. */
    severityLabelKey: 'register.field.benefit',
    dueOnLabelKey: 'register.field.actBy',
    occurredOnLabelKey: null,
    statuses: ['open', 'in_progress', 'resolved', 'closed', 'rejected'],
    attributesSchema: opportunityAttributes,
    attributeFields: [
      {
        key: 'action',
        labelKey: 'register.attribute.action',
        maxLength: 2000,
        multiline: true,
        format: 'markdown',
      },
    ],
    attributesVersion: 1,
    order: 20,
  },
  idea: {
    kind: 'idea',
    labelKey: 'register.kind.idea',
    pluralKey: 'register.kind.idea_plural',
    referencePrefix: 'I',
    /* No dates. An idea that has acquired a due date has become a task, and the
     * product has a better place to keep one of those. */
    fields: { severity: true, probability: false, dueOn: false, occurredOn: false },
    severityLabelKey: 'register.field.priority',
    /* No deadline, because the kind has no `dueOn` -- see the comment above. */
    dueOnLabelKey: null,
    occurredOnLabelKey: null,
    statuses: ['open', 'in_progress', 'resolved', 'rejected'],
    attributesSchema: ideaAttributes,
    attributeFields: [
      {
        key: 'benefit',
        labelKey: 'register.attribute.benefit',
        maxLength: 2000,
        multiline: true,
        format: 'markdown',
      },
    ],
    attributesVersion: 1,
    order: 30,
  },
  decision: {
    kind: 'decision',
    labelKey: 'register.kind.decision',
    pluralKey: 'register.kind.decision_plural',
    referencePrefix: 'D',
    /*
     * A decision has no severity and no probability: either it has been taken
     * or somebody is waiting for it. `occurredOn` is the date it was taken,
     * which is not the date the row was created -- minutes are frequently
     * written up days later.
     *
     * `dueOn` is the other half of that sentence and reads "decide by": the
     * date a decision is *needed* by, which is what makes a requested decision
     * something a project can track rather than something that is either
     * present or absent. An item with a `dueOn` in the past and no `occurredOn`
     * is precisely the decision somebody is still waiting for
     * (docs/register-collaboration-design.md section 5.1).
     */
    fields: { severity: false, probability: false, dueOn: true, occurredOn: true },
    severityLabelKey: null,
    dueOnLabelKey: 'register.field.decideBy',
    occurredOnLabelKey: 'register.field.decidedOn',
    /* Three statuses. A decision is taken, or it is superseded, or it was
     * proposed and not taken. "In progress" is not a state a decision is in. */
    statuses: ['open', 'closed', 'rejected'],
    attributesSchema: decisionAttributes,
    attributeFields: [
      {
        key: 'rationale',
        labelKey: 'register.attribute.rationale',
        maxLength: 4000,
        multiline: true,
        format: 'markdown',
      },
      {
        key: 'decidedBy',
        labelKey: 'register.attribute.decidedBy',
        maxLength: 200,
        multiline: false,
      },
    ],
    attributesVersion: 1,
    order: 40,
  },
  issue: {
    kind: 'issue',
    labelKey: 'register.kind.issue',
    pluralKey: 'register.kind.issue_plural',
    referencePrefix: 'S',
    /* No probability: an issue is a risk that has happened, so its probability
     * is one and saying so adds nothing. */
    fields: { severity: true, probability: false, dueOn: true, occurredOn: false },
    severityLabelKey: 'register.field.severity',
    dueOnLabelKey: 'register.field.resolveBy',
    occurredOnLabelKey: null,
    statuses: ['open', 'in_progress', 'resolved', 'closed'],
    attributesSchema: issueAttributes,
    attributeFields: [
      {
        key: 'impact',
        labelKey: 'register.attribute.impact',
        maxLength: 2000,
        multiline: true,
        format: 'markdown',
      },
    ],
    attributesVersion: 1,
    order: 50,
  },
  observation: {
    kind: 'observation',
    labelKey: 'register.kind.observation',
    pluralKey: 'register.kind.observation_plural',
    /* `O` is taken by opportunity. `H` for hazard is the letter this register is
       actually quoted by on site, and it is unambiguous against the other five. */
    referencePrefix: 'H',
    /*
     * Severity, no probability. An observation is something somebody *saw*, so
     * its probability is one -- the same argument that leaves an issue without
     * one. How bad it is remains a real question and is the number the register
     * is sorted by.
     *
     * Both dates, and they are the two halves of a safety register's whole
     * purpose: `occurredOn` is when it was seen, which is reliably earlier than
     * when it was written up, and `dueOn` reads "close out by". An observation
     * with a `dueOn` in the past and a status of `open` is the one somebody has
     * to answer for.
     */
    fields: { severity: true, probability: false, dueOn: true, occurredOn: true },
    severityLabelKey: 'register.field.severity',
    dueOnLabelKey: 'register.field.closeOutBy',
    occurredOnLabelKey: 'register.field.observedOn',
    /* No `rejected`. An observation is a record of something that was seen, and
       a register that lets an inconvenient one be dismissed is a register nobody
       trusts. It can be closed as needing nothing, which is `closed`, but it is
       never denied. */
    statuses: ['open', 'in_progress', 'resolved', 'closed'],
    attributesSchema: observationAttributes,
    attributeFields: [
      {
        key: 'location',
        labelKey: 'register.attribute.location',
        maxLength: 500,
        multiline: false,
      },
      {
        key: 'immediateAction',
        labelKey: 'register.attribute.immediateAction',
        maxLength: 2000,
        multiline: true,
        format: 'markdown',
      },
      {
        key: 'correctiveAction',
        labelKey: 'register.attribute.correctiveAction',
        maxLength: 2000,
        multiline: true,
        format: 'markdown',
      },
    ],
    attributesVersion: 1,
    order: 60,
  },
} as const satisfies Record<RegisterKind, RegisterKindDefinition>;

export const REGISTER_KIND_DEFINITIONS: readonly RegisterKindDefinition[] = Object.values(
  REGISTER_KIND_CATALOG,
).sort((a, b) => a.order - b.order);

export function registerKindDefinition(kind: RegisterKind): RegisterKindDefinition {
  return REGISTER_KIND_CATALOG[kind];
}

/** The reference a reader quotes: `R-014`. Derived from the stored sequence so
 *  that changing a prefix is a catalog edit rather than an update statement. */
export function registerReference(kind: RegisterKind, sequence: number): string {
  return `${REGISTER_KIND_CATALOG[kind].referencePrefix}-${String(sequence).padStart(3, '0')}`;
}

/** Whether a kind offers a status at all. Enforced on write, so a decision
 *  cannot be put "in progress" by a client that ignored the form. */
export function isStatusAllowedForKind(kind: RegisterKind, status: RegisterStatus): boolean {
  return (REGISTER_KIND_CATALOG[kind].statuses as readonly RegisterStatus[]).includes(status);
}

/**
 * The ordinal scale, shared by every kind that has one.
 *
 * Five points, because a register whose scale has ten is a register whose
 * entries are all sixes and sevens. The labels are per kind -- a risk's 5 is
 * "severe", an idea's 5 is "do it now" -- which is what `severityLabelKey`
 * points at.
 */
export const REGISTER_ORDINAL_MIN = 1;
export const REGISTER_ORDINAL_MAX = 5;
const ordinalSchema = z.number().int().min(REGISTER_ORDINAL_MIN).max(REGISTER_ORDINAL_MAX);

/**
 * How urgent an item looks, as a tone.
 *
 * Two inputs, and the order they are consulted matters. A closed item is
 * finished whatever its severity was, so status wins first; among open items,
 * exposure -- severity times probability, where the kind has both -- decides
 * between warning and danger.
 *
 * Pure, and exported, because the list, the detail panel, the meeting agenda
 * card and the dashboard tile all show the same item and must not disagree
 * about its colour. Colour is never the only carrier: everything drawn in a
 * tone also states its status in words (section 18).
 */
export function registerItemTone(input: {
  kind: RegisterKind;
  status: RegisterStatus;
  severity: number | null;
  probability: number | null;
}): StatusTone {
  if (input.status === 'resolved') return 'success';
  if (input.status === 'closed' || input.status === 'rejected') return 'neutral';

  const definition = REGISTER_KIND_CATALOG[input.kind];
  if (!definition.fields.severity || input.severity === null) return 'info';

  /*
   * An opportunity is the one kind whose high ordinal is good news, so it never
   * reaches `danger`: a prize worth having drawn in the same colour as a
   * hazard is a register nobody can skim.
   */
  if (input.kind === 'opportunity') return input.severity >= 4 ? 'success' : 'info';

  const exposure = definition.fields.probability
    ? input.severity * (input.probability ?? REGISTER_ORDINAL_MAX)
    : input.severity * REGISTER_ORDINAL_MAX;

  if (exposure >= 16) return 'danger';
  if (exposure >= 9) return 'warning';
  return 'info';
}

/* ----------------------------------------------------------- wire: reading */

export const registerItemSchema = z.object({
  id: uuidSchema,
  projectId: uuidSchema,
  kind: registerKindSchema,
  /** Derived from the sequence; see `registerReference`. Sent resolved so no
   *  consumer has to know how a reference is spelled. */
  reference: z.string(),
  sequence: z.number().int().positive(),

  title: z.string(),
  description: z.string().nullable(),
  status: registerStatusSchema,
  statusTone: statusToneSchema,

  ownerUserId: uuidSchema.nullable(),
  severity: z.number().int().nullable(),
  probability: z.number().int().nullable(),
  dueOn: z.string().nullable(),
  occurredOn: z.string().nullable(),

  /** Validated against the kind's declared schema on write; passed through
   *  here, because the reader's form is generated from the same declaration. */
  attributes: z.record(z.string(), z.unknown()),

  version: z.number().int().nonnegative(),
  createdByUserId: uuidSchema.nullable(),
  updatedByUserId: uuidSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type RegisterItem = z.infer<typeof registerItemSchema>;

export const registerItemPageSchema = pageSchema(registerItemSchema);

/* ---------------------------------------------------------- wire: mutation */

const isoDateSchema = z.string().date();

const registerItemWritableFields = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(8000).nullish(),
  status: registerStatusSchema,
  ownerUserId: uuidSchema.nullish(),
  severity: ordinalSchema.nullish(),
  probability: ordinalSchema.nullish(),
  dueOn: isoDateSchema.nullish(),
  occurredOn: isoDateSchema.nullish(),
  /**
   * Checked against the kind's schema in the service rather than here, because
   * the kind is a sibling member and Zod cannot reach across to it without
   * turning every request schema into a discriminated union of five. The
   * service knows the kind for certain -- on update it reads it from the stored
   * row rather than trusting the body -- which is the stronger place to check.
   */
  attributes: z.record(z.string(), z.unknown()).optional(),
});

export const createRegisterItemSchema = registerItemWritableFields
  .extend({
    kind: registerKindSchema,
    status: registerStatusSchema.default('open'),
    /**
     * An identifier the capturing device generated, when one did.
     *
     * Makes the create idempotent: an observation written on site with no
     * signal is queued on the phone and submitted later, possibly more than
     * once, because a request that timed out may well have succeeded. Sending
     * the same reference twice returns the first row rather than filing the
     * same near-miss twice.
     *
     * Absent for everything typed into the web interface, which is online by
     * definition and has a form that clears itself on success.
     */
    clientRef: idempotencyKeySchema.optional(),
  })
  .strict();
export type CreateRegisterItem = z.infer<typeof createRegisterItemSchema>;

/**
 * Editing an item: everything except its kind.
 *
 * The kind is immutable. A risk that turned out to be an issue is a new row
 * that supersedes the old one, not a relabelled one: the reference `R-014` has
 * been quoted in minutes by then, and silently turning it into `S-014` breaks
 * every reference to it -- including the ones on paper.
 */
export const updateRegisterItemSchema = registerItemWritableFields
  .partial()
  .extend(versionedSchema.shape)
  .strict();
export type UpdateRegisterItem = z.infer<typeof updateRegisterItemSchema>;

export const registerListQuerySchema = z
  .object({
    kind: registerKindSchema.optional(),
    status: registerStatusSchema.optional(),
    ownerUserId: uuidSchema.optional(),
    /** True to hide everything that is finished, which is the default reading
     *  of a register: it is a list of what is still open. */
    openOnly: z.coerce.boolean().optional(),
    search: z.string().max(120).optional(),
    cursor: z.string().max(256).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .strict();
export type RegisterListQuery = z.infer<typeof registerListQuerySchema>;

/**
 * Counts per kind, for the dashboard tile.
 *
 * Its own shape rather than a page of items with a total, because the tile
 * needs five numbers and the list needs rows -- and serving the first from the
 * second means downloading a project's whole risk register in order to render
 * "4 open".
 */
export const registerSummarySchema = z.array(
  z.object({
    kind: registerKindSchema,
    open: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
  }),
);
export type RegisterSummary = z.infer<typeof registerSummarySchema>;

/** The statuses `openOnly` keeps. Exported so the list screen's summary counts
 *  and the API's filter cannot disagree about what "open" means. */
export const OPEN_REGISTER_STATUSES: readonly RegisterStatus[] = ['open', 'in_progress'];

export function isOpenRegisterStatus(status: RegisterStatus): boolean {
  return OPEN_REGISTER_STATUSES.includes(status);
}

/* ------------------------------------------------------- change tracking */

/**
 * The fields a revision of this kind records (`diffRecords` in revisions.ts).
 *
 * Derived from the catalog rather than listed, which is the point: a kind that
 * gains an ordinal, a deadline or an attribute gains history for it in the same
 * edit, and a kind that never had one never records "severity: null -> null".
 *
 * `version`, the timestamps and the actor are deliberately absent. They change
 * on every save by definition, and a history in which every entry says "the
 * version changed" is a history with one useful column and four decorative
 * ones.
 */
export function registerTrackedFields(kind: RegisterKind): string[] {
  const definition = REGISTER_KIND_CATALOG[kind];
  const fields = ['title', 'description', 'status', 'ownerUserId'];

  if (definition.fields.severity) fields.push('severity');
  if (definition.fields.probability) fields.push('probability');
  if (definition.fields.dueOn) fields.push('dueOn');
  if (definition.fields.occurredOn) fields.push('occurredOn');

  return [...fields, ...definition.attributeFields.map((field) => `attributes.${field.key}`)];
}
